import type {Coordinate} from 'ol/coordinate';
import type LineString from 'ol/geom/LineString';
import type MultiLineString from 'ol/geom/MultiLineString';
import Point from 'ol/geom/Point.js';
import Style from 'ol/style/Style.js';
import StyleIcon from 'ol/style/Icon.js';
import type Feature from 'ol/Feature';
import {computeRotation as computeMathematicRotation} from './arrowHelper.ts';

type IconConfig = {path: string; width: number; height: number; key: string};

const ARROW_ICON_PATH = '/img/highlight/arrow.svg?ts11=TIMESTAMP11';
const ARROW_ICON_PATH_SMALL = '/img/highlight/arrow_small.svg?ts11=TIMESTAMP11';
const ARROW_ICON_SIZE = [16, 16];

export function getArrowStyle(
  feature: Feature<MultiLineString>,
  resolution: number,
): Style[] {
  const smallResolution = resolution <= 20;
  const arrowInterval = resolution * (smallResolution ? 60 : 20);
  const path = smallResolution ? ARROW_ICON_PATH : ARROW_ICON_PATH_SMALL;

  const styles = feature
    .getGeometry()
    .getLineStrings()
    .map((lineString) => {
      return lineStringToArrows(
        lineString,
        arrowInterval,
        (position: Coordinate, rotation: number) => {
          return new Style({
            geometry: new Point(position),
            image: getArrowIconForRotation(rotation, {
              key: path,
              path: path,
              width: ARROW_ICON_SIZE[0],
              height: ARROW_ICON_SIZE[1],
            }),
          });
        },
      );
    });

  return styles.flat();
}

const scratchStart: Coordinate = [];
const scratchEnd: Coordinate = [];
export function lineStringToArrows(
  line: LineString,
  arrowIntervalMeters: number,
  arrowFactory: (position: Coordinate, rotation: number) => Style,
  extent?: number[],
): Style[] {
  const intervalHalfLength = arrowIntervalMeters / 2;
  const length = line.getLength();
  const nPoints = Math.floor(length / intervalHalfLength) + 1;
  const nArrows = Math.floor(nPoints / 2);
  const styles: Style[] = [];
  const metersApart = 3; // take the 2nd point a few meters away
  // Skip the arrow at start of the line
  for (let i = 1; i < nArrows; i++) {
    const distanceFromStart = 2 * i * intervalHalfLength;
    const start = line.getCoordinateAt(
      distanceFromStart / length,
      scratchStart,
    );
    const end = line.getCoordinateAt(
      (distanceFromStart + metersApart) / length,
      scratchEnd,
    );
    const rotation = computeMathematicRotation(start, end);
    styles.push(arrowFactory(start, rotation));
    // Keep this: it is useful to debug rotation
    // styles.push(new Style({
    //   geometry: new Point(start),
    //   text: new Text({
    //     text: String(Math.floor(rotation * 180 / Math.PI)),
    //     offsetX: 35,
    //     font: '16px sans-serif',
    //   }),
    //   image: new Circle({
    //     radius: 3,
    //     fill: new Fill({
    //       color: 'blue'
    //     }),
    //   })
    // }));
    // styles.push(new Style({
    //   geometry: new Point(end),
    //   image: new Circle({
    //     radius: 3,
    //     fill: new Fill({
    //       color: 'purple'
    //     }),
    //   })
    // }));
  }
  return styles;
}

const iconCache: Record<string, StyleIcon> = {};

/**
 *
 * @param rotation Rotation angle in radians [0-2Pi]
 * @param icon icon configuration
 */
export function getArrowIconForRotation(
  rotation: number,
  icon: IconConfig,
): StyleIcon {
  const rotationFactor = 100 / (2 * Math.PI);
  const quantizedRotation =
    Math.round(rotation * rotationFactor) / rotationFactor;
  const cacheKey = `${icon.key}-${quantizedRotation.toFixed(2)}`;
  if (!iconCache[cacheKey]) {
    iconCache[cacheKey] = new StyleIcon({
      width: icon.width,
      height: icon.height,
      src: icon.path,
      // we take the opposite because the canvas does not use the usual orientation for the axis
      // see https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Drawing_shapes#the_grid
      // y points to the bottom
      rotation: -quantizedRotation,
    });
  }
  return iconCache[cacheKey];
}
